export const beforeItems = [
  "You review setups without a consistent checklist.",
  "You spend too much time going back and forth between different resources.",
  "You feel uncertain when evaluating space creation, attachments, or planned movements.",
  "You struggle to organize your clinical questions before communicating with the treatment-planning team.",
  "You worry that important considerations may be overlooked before approval.",
  "You find yourself approaching similar cases differently because you lack a standardized review process.",
];

export const afterItems = [
  "Follow a structured review sequence before approving a setup.",
  "Use visual frameworks to organize your clinical reasoning.",
  "Review space requirements, planned movements, and attachment strategies systematically.",
  "Prepare clearer, more specific questions for your treatment-planning team.",
  "Document your observations and identify areas that may require additional assessment.",
  "Build a repeatable workflow that supports more consistent clinical reviews.",
];

export const painPoints = [
  {
    title: "You hesitate before approving a ClinCheck.",
    text: "You want to feel confident that you've reviewed the important aspects of the proposed treatment, but you're not always sure what deserves another look.",
  },
  {
    title: "You spend too much time reviewing cases.",
    text: "You revisit the same questions, search through different resources, and struggle to follow a consistent review sequence.",
  },
  {
    title: "You're uncertain when evaluating space creation.",
    text: "You want a more organized way to assess considerations involving IPR, expansion, distalization, and other treatment-planning alternatives.",
  },
  {
    title: "You want to identify potential problems earlier.",
    text: "You understand that treatment planning decisions matter, but you need a clearer framework for documenting concerns before approval.",
  },
  {
    title: "You depend too heavily on the software or technician.",
    text: "You want to develop your own structured clinical review process rather than relying exclusively on the proposed digital setup.",
  },
  {
    title: "Complex cases leave you with more questions than answers.",
    text: "You want a practical way to organize your clinical reasoning and determine when a case requires additional expertise or assessment.",
  },
];

export const toolkit = [
  { title: "Clinical Review Checklists", text: "Organize your review around defined questions instead of relying exclusively on memory." },
  { title: "Visual Decision Maps", text: "Use diagrams to organize clinical considerations and identify questions requiring further assessment." },
  { title: "Case Assessment Worksheets", text: "Document relevant findings, treatment objectives, potential limitations, and outstanding questions." },
  { title: "Treatment Planning Communication Templates", text: "Prepare clearer, more structured communications when discussing a setup with your treatment-planning team." },
  { title: "Progress and Finishing Review Sheets", text: "Keep important review points organized throughout the treatment process." },
];

export const expectations = [
  { title: "A Clearer Workflow", text: "Follow an organized sequence for reviewing a case." },
  { title: "Visual Clinical References", text: "Consult diagrams, maps, and worksheets when organizing your questions." },
  { title: "More Structured Communication", text: "Document your observations and communicate them more clearly to your treatment-planning team." },
  { title: "Reusable Professional Tools", text: "Return to the templates whenever they are relevant to your workflow." },
];

export const offerIncludes = [
  "Immediate digital access",
  "Six specialized clinical blueprints",
  "Five professional bonus resources",
  "Downloadable reference materials",
  "No subscriptions or recurring fees",
  "Seven-day money-back guarantee",
];

export const finalIncludes = [
  "6 specialized clinical blueprints",
  "Visual decision-making frameworks",
  "Practical review checklists",
  "Clinical planning worksheets",
  "5 exclusive bonus resources",
  "Immediate digital access",
  "Seven-day money-back guarantee",
];

export const guaranteeItems = [
  "Explore all six volumes.",
  "Review the visual frameworks.",
  "Examine the clinical checklists.",
  "Use the planning worksheets.",
  "Evaluate the bonus resources.",
];

export const faqs = [
  { q: "How will I receive the materials?", a: "After completing your purchase, you'll receive instructions for accessing your digital product. The six volumes and five bonuses will be provided in downloadable digital formats." },
  { q: "Is this a physical product?", a: "No. The ClinCheck Blueprint is a digital product. Nothing will be shipped to your address." },
  { q: "Is this a one-time payment?", a: "Yes. The advertised price is a one-time payment of $27 USD, with no recurring subscription charges." },
  { q: "Is the Blueprint compatible with different clear aligner systems?", a: "The materials are designed around general clear aligner treatment-planning considerations rather than being presented as an official guide to one proprietary software platform. Specific software features and workflows may vary by provider." },
  { q: "Do I need to be an orthodontist?", a: "The resource is intended for qualified dental professionals involved in clear aligner treatment planning. Your scope of practice, training, and applicable professional regulations determine which clinical activities you may perform." },
  { q: "Will this system guarantee fewer refinements?", a: "No clinical resource can guarantee that a treatment will avoid refinements. The Blueprint is designed to help organize the review process and document potential concerns. Treatment outcomes depend on many factors, including the individual case, diagnosis, biomechanics, patient compliance, and clinical management." },
  { q: "Do I need special software?", a: "No specialized software is required to consult the digital reference materials and printable worksheets. You can use compatible PDF readers and standard document tools for the resources provided in those formats." },
  { q: "What happens if I'm not satisfied?", a: "Your purchase includes a seven-day money-back guarantee. Contact us within the guarantee period to request a refund in accordance with the applicable terms." },
];

export const testimonials = [
  {
    name: "Dr. Sarah Jenkins",
    role: "General Dentist · Denver, CO",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/629ece9e-cf76-4289-b1d5-ac4d22b9d49f.jpg",
    quote: "I finally have a sequence I follow on every setup. Instead of jumping between notes and old cases, I open the checklist and work through it. My questions to the planning team are much more specific now.",
  },
  {
    name: "Dr. Michael Harlow",
    role: "Family Dentistry · Columbus, OH",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/c54e2044-a8d1-412d-9de4-bf08729d2900.jpg",
    quote: "The space management matrix alone was worth it. Laying out IPR, expansion and distalization side by side made my documentation so much cleaner.",
  },
  {
    name: "Dr. Camila Ortega",
    role: "Orthodontist · Miami, FL",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/26c5e097-9b14-4f3e-a253-86f6824f0c19.jpg",
    quote: "I share the communication templates with my associates. Everyone now reviews cases in the same order, which makes our case discussions faster and far more organized.",
  },
  {
    name: "Dr. Andre Williams",
    role: "Cosmetic Dentist · Atlanta, GA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/770f7ff9-7591-4fc7-b313-37a23d9b4daa.jpg",
    quote: "It's not a course I'll never finish. It's a reference I keep open while reviewing. The patient scripts for attachments and IPR have been really practical too.",
  },
];

export const buyers = [
  { name: "Valentina R.", country: "Mexico" },
  { name: "Santiago M.", country: "Colombia" },
  { name: "Lucía F.", country: "Argentina" },
  { name: "Mateo G.", country: "Chile" },
  { name: "Camila T.", country: "Peru" },
  { name: "Diego A.", country: "Spain" },
  { name: "Isabella P.", country: "Ecuador" },
  { name: "Andrés C.", country: "Venezuela" },
  { name: "Mariana L.", country: "Uruguay" },
  { name: "Javier H.", country: "Costa Rica" },
  { name: "Sofía B.", country: "Dominican Republic" },
  { name: "Gabriel V.", country: "Guatemala" },
  { name: "Daniela O.", country: "Bolivia" },
  { name: "Alejandro N.", country: "Panama" },
  { name: "Paula S.", country: "Paraguay" },
];
